import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import { Spinner } from "@/components/ds";
import { RotaGrid } from "@/components/app/rota/RotaGrid";
import { usePageTitle } from "../hooks/usePageTitle";
import type { Shift } from "@/domain/shifts/types";

interface RotaData {
  readonly shifts: Shift[];
  readonly sites: readonly { id: string; name: string }[];
  readonly cleaners: readonly { membershipId: string; name: string; email: string; pending: boolean }[];
  readonly weekStart: string;
  readonly draftCount: number;
}

function getWeekStart(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

export function RotaPage() {
  usePageTitle("Rota | TidyRota");
  const [searchParams] = useSearchParams();
  const week = searchParams.get("week") ?? getWeekStart();
  const [data, setData] = useState<RotaData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetch(`/api/shifts?week=${week}`)
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setData(result.data);
        }
      })
      .finally(() => setLoading(false));
  }, [week]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-10)" }}>
        <Spinner />
      </div>
    );
  }

  if (!data) return null;

  return (
    <RotaGrid
      shifts={data.shifts}
      sites={data.sites}
      cleaners={data.cleaners}
      weekStart={data.weekStart}
      draftCount={data.draftCount}
      onRefresh={fetchData}
    />
  );
}
